import React, { memo, useReducer, useMemo, useCallback } from 'react';
import { withPopUp } from '@redlink/components/atoms';
import { LinkTemplateList } from '@redlink/components/muscles';
import { useLocale } from '@redlink/assets/locale';
import { DataEntryModalTemplate } from '@redlink/components/templates';
import { editShopContactsQuery, useMutation } from '@redlink/services';
import { Form } from '@redlink/components/organisms';
import { useValues } from '@redlink/assets/values';
import { initialState, reducer, types } from './store';
import { freshCache } from './cache';

export const ShopContactEditPage = withPopUp(
  memo(({ onDismiss, pageName, data }) => {
    const { dictionary } = useLocale();
    const { staticData } = useValues();

    const transformedTemplates = useMemo(() => {
      return staticData.shopContactTemplates.reduce(
        (prev, { title, items }, index) => {
          const result = items.reduce((p, item) => {
            const transformedItem = {
              parent: title,
              parentOrder: index,
              ...item,
            };

            const existItems = data
              .filter((existItem) => existItem.type === transformedItem.title)
              .map(({ id, type, raw, url, logoName }, index) => ({
                ...transformedItem,
                isNewItem: true,
                value: { url, raw, logoName, title: type },
              }));

            return [...p, transformedItem, ...existItems];
          }, []);

          return [...prev, ...result];
        },
        []
      );
    }, [data]);

    const [{ items: mergedItems }, dispatch] = useReducer(
      reducer,
      initialState({
        items: transformedTemplates,
      })
    );

    const [
      submitEditShopContacts,
      { loading: submitEditShopContactsIsLoading },
    ] = useMutation(editShopContactsQuery, {
      done: () => onDismiss(),
      update: freshCache({ pageName }),
    });

    const onSubmit = useCallback(() => {
      const normalizedItems = mergedItems
        .filter(({ value: { raw = '' } = {} }) => raw.trim() !== '')
        .sort((a, b) => a.parentOrder - b.parentOrder)
        .map(({ value: { title, ...rest } }) => ({
          type: title,
          ...rest,
        }));

      submitEditShopContacts({
        contacts: normalizedItems,
      });
    }, [mergedItems, submitEditShopContacts]);

    return (
      <DataEntryModalTemplate>
        <>
          <Form
            isBusy={submitEditShopContactsIsLoading}
            actions={[
              {
                title: dictionary.cancel,
                variant: 'secondary',
                onPress: onDismiss,
              },
              {
                title: dictionary.add,
                variant: 'primary',
                onPress: onSubmit,
              },
            ]}
          >
            {staticData.shopContactTemplates.map(({ items, title }, index) => {
              const filteredItems = [
                ...mergedItems.filter(({ parent }) => parent === title),
              ];

              return (
                <LinkTemplateList
                  data={filteredItems}
                  title={dictionary[title]}
                  onChange={(value) => {
                    const restItems = mergedItems.filter(
                      (p) => p.parent !== title
                    );
                    dispatch({
                      type: types.SET_ITEMS,
                      payload: [...restItems, ...value],
                    });
                  }}
                />
              );
            })}
          </Form>
        </>
      </DataEntryModalTemplate>
    );
  })
);
